/* eslint-disable no-console */
import React, { Component } from 'react'
import { connect } from 'react-redux'
import styled, { ThemeProvider } from 'styled-components'
import PropTypes from 'prop-types'
import SearchResult from '../components/SearchResult'
import SearchResultWide from '../components/SearchResultWide'
import CantFindResult from '../components/CantFindResult'
import { theme } from '../settings'
import { colors, fontFamilies } from '../../search-common/settings'
import { media, getPropertyImages } from '../../search-common/helpers'
import { fetchImagesIfNotBusy } from '../actions/images'
import VisibilityFilters from './VisibilityFilters'
import LoadingOverlay from './LoadingOverlay'
import { translate } from '../../modules/helper-functions'
import SEOContent from '../components/SEOContent'
import VisibilitySensor from 'react-visibility-sensor'
import Spinner from '../components/Spinner'

const INITIALLY_VISIBLE = 9
const LOAD_MORE_AMOUNT = 6

const Container = styled.div`
  background-color: ${colors.grayTone1};
  padding-bottom: 60px;
  position: relative;
`

const Wrapper = styled.div`
  position: relative;
  margin: 0 auto;
  max-width: 1440px;
  width: 100%;
  color: ${colors.white};
  display: grid;
  grid-gap: 20px;
  padding: 20px;
  padding-bottom: 60px;
`

const Item = styled.div`
  border: 1px solid ${colors.grayTone2};
  ${media.tablet`
    grid-row: span 2;
    :nth-child(6n-1),
    :nth-child(6n) {
      grid-column: span 2;
      grid-row: span 1;
      border: none;
    }
  `}
`

const LoadMoreBtn = styled.button`
  background-color: transparent;
  border: 1px solid ${colors.grayTone3};
  color: ${colors.grayTone3};
  font-size: 14px;
  text-transform: uppercase;
  margin: 0 auto 60px;
  display: block;
  width: 50%;
  max-width: 250px;
  font-family: ${fontFamilies.aeonik};
  font-weight: bold;
  text-align: center;
  padding: 20px 30px;
  cursor: pointer;
  &:hover:not(:disabled) {
    color: ${colors.highlight};
    border-color: ${colors.highlight};
  }
  &:disabled {
    opacity: 0.5;
  }

  svg {
    width: 20px;
    height: 20px;
  }
`

const AlsoFoundWrapper = styled.h3`
  font-size: 24px;
  margin-left: 20px;
`

/**
 *
 */
const isLarge = ( index ) => {
  return ( index + 1 ) % 6 === 0 || ( index + 1 ) % 6 === 5
}

/**
 * SearchResults container.
 */
class SearchResults extends Component {
  /**
   * Constructor
   *
   * @param {object} props
   */
  constructor( props ) {
    super( props )

    this.state = {
      itemsVisible: INITIALLY_VISIBLE
    }

    this.loadMoreResults = this.loadMoreResults.bind( this )
    this.visibilityChange = this.visibilityChange.bind( this )
  }

  /**
   * Function loads more search results.
   */
  loadMoreResults() {
    const { itemsVisible } = this.state
    this.setState({
      itemsVisible: itemsVisible + LOAD_MORE_AMOUNT
    })
  }

  /**
   * onChange handler for visibility sensor.
   *
   * @param {bool} isVisible
   */
  async visibilityChange( isVisible ) {
    if ( isVisible ) {
      this.loadMoreResults()
    }
  }

  /**
   * componentDidMount function
   */
  componentDidMount() {
    const { getImages } = this.props
    getImages()
  }

  /**
   * Render function.
   */
  render() {
    const { results, unfilteredResults, isFetching, images, translations, locale } = this.props
    const { itemsVisible } = this.state

    const unfiltered = unfilteredResults.filter(( res ) => !results.some(( prop ) => prop.pid === res.pid ))
    const hasResults = results.length > 0
    const hasUnfiltered = unfiltered.length > 0
    const showUnavailableResults = hasUnfiltered || !hasResults

    return (
      <ThemeProvider theme={theme}>
        <Container>
          <LoadingOverlay isFetching={isFetching} />
          <VisibilityFilters resultsAmount={results.length} />
          <SEOContent topContent />
          {hasResults && ( <Wrapper className="c-grid tablet--2 desktop--3 wide--4">
            {results.slice( 0, itemsVisible ).map(( result, index ) => (
              <Item key={result.uuid}>
                {isLarge( index )
                  ? <SearchResultWide locale={locale} result={result} translations={translations} images={getPropertyImages( result.uuid, images )} />
                  : <SearchResult locale={locale} result={result} translations={translations} images={getPropertyImages( result.uuid, images )} />
                }
              </Item>
            ))}
            <CantFindResult />
          </Wrapper> )}
          {itemsVisible < results.length + 4
            ? (
              <VisibilitySensor onChange={this.visibilityChange}>
                <LoadMoreBtn disabled={isFetching} onClick={this.loadMoreResults}>
                  {isFetching ? <Spinner /> : translate( translations, 'Load more' )}
                </LoadMoreBtn>
              </VisibilitySensor>
              )
            : null
          }
          {hasUnfiltered ? (
            <AlsoFoundWrapper>{translate( translations, 'We also found these' )}</AlsoFoundWrapper>
          ) : ''}
          {showUnavailableResults && ( <Wrapper className="c-grid tablet--2 desktop--3 wide--4">
            {unfiltered.map(( result, index ) => (
              <Item key={result.uuid}>
                {isLarge( index )
                  ? <SearchResultWide locale={locale} result={result} notAvailable translations={translations} images={getPropertyImages( result.uuid, images )} />
                  : <SearchResult locale={locale} result={result} notAvailable translations={translations} images={getPropertyImages( result.uuid, images )} />
                }
              </Item>
            ))}
            <CantFindResult />
          </Wrapper> )}
          <SEOContent />
        </Container>
      </ThemeProvider>
    )
  }
}

SearchResults.propTypes = {
  results: PropTypes.array.isRequired,
  unfilteredResults: PropTypes.array.isRequired,
  images: PropTypes.array.isRequired,
  isFetching: PropTypes.bool.isRequired,
  getImages: PropTypes.func.isRequired,
  translations: PropTypes.object.isRequired,
  locale: PropTypes.string.isRequired
}

/**
 * Map the redux state to component props.
 *
 * @param {object} state
 * @return {object}
 */
const mapStateToProps = ( state ) => ({
  results: state.search.results,
  unfilteredResults: state.search.unfilteredResults,
  images: state.images.data,
  isFetching: state.search.isFetching,
  translations: state.translations,
  locale: state.locale
})

/**
 * Add dispatch functions to components props.
 *
 * @param {function} dispatch
 * @return {object}
 */
const mapDispatchToProps = ( dispatch ) => {
  return {
    getImages: () => {
      dispatch( fetchImagesIfNotBusy())
    }
  }
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)( SearchResults )
